import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/breadcrumb.js';
import '../components/empty_state.js';
import '../components/error_state.js';
import '../components/fertilizer_plan_list.js';
import '../components/fertilizer_product_create.js';
import '../components/fertilizer_product_detail.js';
import '../components/fertilizer_product_list.js';
import '../components/grazing_record_list.js';
import '../components/plant_protection_list.js';
import '../components/load_error_callout.js';
import '../components/mineral_dose_create.js';
import '../components/mineral_dose_detail.js';
import '../components/mineral_fertilizer_product_create.js';
import '../components/mineral_fertilizer_product_detail.js';
import '../components/mineral_fertilizer_product_list.js';
import '../components/nitrogen_adjustment_list.js';
import '../components/parcel_create.js';
import '../components/parcel_crop_detail.js';
import '../components/parcel_crop_list.js';
import '../components/parcel_list.js';
import '../components/parcel_master_data.js';
import '../components/preceding_crop_detail.js';
import '../components/preceding_crop_list.js';
import '../components/prior_organic_application_detail.js';
import '../components/prior_organic_application_list.js';
import '../components/soil_sample_detail.js';
import '../components/soil_sample_list.js';
import '../components/yield_series_create.js';
import '../components/yield_series_detail.js';
import '../components/yield_series_list.js';

import { faCircleExclamation, faLock, faPersonDigging } from '@fortawesome/free-solid-svg-icons';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import type { IBreadcrumbItem } from '../components/breadcrumb.js';
import type { ICrop } from '../libraries/api_client/crop.js';
import {
    getFarmById,
    type IFarm,
} from '../libraries/api_client/farm.js';
import type { ApplicationKind } from '../libraries/api_client/fertilizer_application.js';
import type { FertilizerKind } from '../libraries/api_client/fertilizer_product.js';
import {
    type IFertilizerYear,
    listFertilizerYearsByFarmId,
} from '../libraries/api_client/fertilizer_year.js';
import {
    getOrganization,
    type IOrganization,
} from '../libraries/api_client/organization.js';
import type {
    ApiResult,
    IApiFailure,
} from '../libraries/api_client/request.js';
import type {
    IDisplayNameDetail,
    IDisplayNameFailedDetail,
    IFertilizerPlanCropFollowedDetail,
    IYieldSeriesCropChosenDetail,
} from '../libraries/component_events.js';
import {
    addressedParcelCropId,
    cultivationDataListView,
    type CultivationDataView,
    resolveEffectiveCultivationDataView,
} from '../libraries/cultivation_data_view.js';
import { detailPageStyles, detailPageTabs } from '../libraries/detail_page.js';
import { setDocumentTitle } from '../libraries/document_title.js';
import {
    addressedFertilizerApplicationSubjectId,
    fertilizerApplicationListView,
    type FertilizerApplicationView,
    resolveEffectiveFertilizerApplicationView,
} from '../libraries/fertilizer_application_view.js';
import {
    applicationKindCopy,
} from '../libraries/fertilizer_plan_labels.js';
import {
    addressedFertilizerProductId,
    fertilizerProductListView,
    type FertilizerProductView,
    resolveEffectiveFertilizerProductView,
} from '../libraries/fertilizer_product_view.js';
import {
    cultivationDataAreaSlug,
    documentationAreaSlug,
    fertilizerPlanAreaSlug,
    fertilizersAreaSlug,
    grazingAnimalsSubAreaSlug,
    type IFertilizerYearArea,
    type IFertilizerYearSubArea,
    mineralFertilizationSubAreaSlug,
    mineralSubAreaSlug,
    nitrateVulnerableZoneSubAreaSlug,
    nitrogenAdjustmentAreaSlug,
    organicFertilizationSubAreaSlug,
    organicSubAreaSlug,
    parcelsAreaSlug,
    plantProtectionSubAreaSlug,
    previousCropsSubAreaSlug,
    previousYearAreaSlug,
    soilSamplesAreaSlug,
    yieldsAreaSlug,
    yieldSubAreaSlug,
} from '../libraries/fertilizer_year_areas.js';
import { forbiddenStatus, notFoundStatus } from '../libraries/http_status.js';
import { redirectTo } from '../libraries/navigation.js';
import { cropRecordNoun } from '../libraries/parcel_crop_labels.js';
import { parcelListView, type ParcelView, resolveEffectiveParcelView } from '../libraries/parcel_view.js';
import { canDocumentFarm, canImportIntoFarm, canWriteFarm } from '../libraries/permissions.js';
import {
    previousYearListView,
    type PreviousYearView,
    resolveEffectivePreviousYearView,
} from '../libraries/previous_year_view.js';
import {
    cardSkeleton,
    headingSkeleton,
    liveRegion,
    skeletonStyles,
    tabStripRowSkeleton,
} from '../libraries/skeletons.js';
import {
    resolveEffectiveSoilSampleView,
    soilSampleListView,
    type SoilSampleView,
} from '../libraries/soil_sample_view.js';
import {
    addressedYieldSeriesId,
    resolveEffectiveYieldSeriesView,
    yieldSeriesListView,
    type YieldSeriesView,
} from '../libraries/yield_series_view.js';
import { resolveYieldWindow } from '../libraries/yield_window.js';

const yearPattern = /^\d{4}$/;

const yearNotFoundHeading = 'Düngejahr nicht gefunden';
const farmNotFoundHeading = 'Betrieb nicht gefunden';
const yearForbiddenHeading = 'Kein Zugriff auf das Düngejahr';

/**
 * The heading of the error state a validation result shows, which names the document in place of the
 * route's own title (WCAG 2.4.2): the page shows that state, not the year.
 * @param result - The validation result.
 * @returns The heading, or null where the result shows the year or is on its way elsewhere.
 */
function errorStateHeading (result: ValidationResult): string | null {
    if (result.status === 'year-not-found') {
        return yearNotFoundHeading;
    }

    if (result.status === 'farm-not-found') {
        return farmNotFoundHeading;
    }

    return result.status === 'failure' && result.failure.status === forbiddenStatus ? yearForbiddenHeading : null;
}

/** The one `FertilizerYearStatus` that admits a write; the other is `inactive`. */
const activeYearStatus = 'active';

/**
 * The Schlag a parcel-scoped screen is about, or `null` for its list.
 *
 * One reading for the areas whose view unions are the same shape, the Bodenuntersuchung and
 * the Vorjahresdaten, so {@link FmFertilizerYearDetail.addressedSubjectId} names each once
 * rather than repeating the branch.
 * @param view - The screen the route addressed.
 * @returns The parcel's id, or null.
 */
function addressedParcelOfView (view: SoilSampleView | PreviousYearView): string | null {
    return view.kind === 'record' ? view.parcelId : null;
}

/** The record half of the Vorjahresdaten's view union. */
type PreviousYearRecordView = Extract<PreviousYearView, { kind: 'record' }>;

/**
 * The screens actually being rendered, resolved once per render.
 *
 * One object rather than one parameter per area, for the reason `app.ts`'s own `IAreaViews`
 * is one: the heading, the lead, the breadcrumb and the body all read them, so a third area
 * with screens of its own adds a key instead of an argument to five signatures.
 */
interface IEffectiveViews {
    parcel: ParcelView;
    soilSample: SoilSampleView;
    fertilizerProduct: FertilizerProductView;
    previousYear: PreviousYearView;
    yieldSeries: YieldSeriesView;
    cultivationData: CultivationDataView;
    fertilizerApplication: FertilizerApplicationView;
}

/** The happy path of one validation pass, and the context every area leaf renders from. */
interface IValidatedYear {
    readonly status: 'validated';
    readonly organization: IOrganization | null;
    readonly farm: IFarm;

    /**
     * The matched year itself, not just the fact that it exists: every parcel call is keyed
     * by the year's id, which the URL carries only as the year number.
     */
    readonly fertilizerYear: IFertilizerYear;

    /**
     * Whether the caller may write in this year, which is farm write access **and** an
     * active year. The same pair `loadWritableFertilizerYear` enforces server-side, so no
     * editing path is offered that the server would refuse. Resolved once here rather than
     * per leaf, because every area below the year needs the same answer.
     */
    readonly canWrite: boolean;

    /**
     * Whether the caller may import a file into this year: the farm's leadership beside the
     * write right, and an active year, which is the pair `importableFertilizerYear`
     * enforces server-side. Resolved here beside `canWrite`, for the same reason.
     */
    readonly canImport: boolean;

    /**
     * Whether the caller may write the Dokumentation of this year: the farm's documentation
     * right, which every writer holds too, and an active year, the pair
     * `documentableFertilizerYear` enforces server-side. Resolved here beside `canWrite`, for
     * the same reason; the documentation's lists take this where the other areas take `canWrite`.
     */
    readonly canDocument: boolean;
}

/** What the caller may do on the farm, as the permission predicates answered it, before the year narrows it. */
interface IFarmAccess {
    writable: boolean;
    importable: boolean;
    documentable: boolean;
}

/**
 * Discriminated outcome of one validation pass. `validated` is the
 * happy path ({@link IValidatedYear}). `year-not-found` is rendered inline as the
 * "Düngejahr nicht gefunden" `<fm-error-state>` — no redirect, so
 * the URL the user shared stays addressable. `farm-not-found` is the
 * year-list 404 case (server message: "Betrieb nicht gefunden.") and
 * renders inline as the "Betrieb nicht gefunden"
 * `<fm-error-state>` — same shape `<fm-farm-detail>` shows when its
 * own primary fetch 404s, so the user sees consistent copy regardless
 * of which endpoint surfaced the missing farm. `failure` carries the
 * `IApiFailure` from year-list 403 / 5xx so the renderer can branch:
 * a 403 status promotes the inline "Kein Zugriff auf
 * das Düngejahr" `<fm-error-state>`; anything else falls through to
 * the skeleton + closable callout with retry. `redirecting`
 * acknowledges a side-effecting branch (malformed input, missing farm
 * /404 from `getFarmById`, organisation failure) whose redirect
 * side-effect ran from inside the Task body.
 */
type ValidationResult =
    | IValidatedYear |
    { readonly status: 'year-not-found';
        readonly organization: IOrganization | null;
        readonly farm: IFarm; } |
    { readonly status: 'farm-not-found' } |
    { readonly status: 'failure';
        readonly failure: IApiFailure; } |
    { readonly status: 'redirecting' };

/** The two areas whose tabs list doses; they share one view union for their mineral dose pages. */
const doseAreaSlugs: ReadonlyArray<string> = [
    fertilizerPlanAreaSlug,
    documentationAreaSlug,
];

/**
 * Whether an area is one of the two that list doses.
 * @param slug - The open area's slug.
 * @returns Whether it is the Düngeplanung or the Dokumentation.
 */
function isDoseArea (slug: string | undefined): boolean {
    return slug !== undefined && doseAreaSlugs.includes(slug);
}

/**
 * The areas whose open screen is named by the entity its leaf reported rather than by a view
 * of its own: each addresses one record and lets that record's name reach the heading, the
 * breadcrumb tail and the lead through one report.
 */
const subjectNamedAreas: ReadonlyArray<string> = [
    soilSamplesAreaSlug,
    previousYearAreaSlug,
    cultivationDataAreaSlug,
];

/**
 * The page of a fertilizer year, mounted both at the year itself
 * (`/.../fertilizer-years/:year`) and at each of its work areas
 * (`.../:year/<area>`). The `area` property selects which the page renders, and its
 * body: the Schlagkartei renders its own leaf, and every area whose epic has not landed
 * yet renders the shared placeholder.
 *
 * The page is the shell for all of them because all of them need the same validated year
 * context, and it resolves that context once: the farm, the organisation, the year entity
 * behind the URL's year number, and whether the caller may write in it. An area route
 * rendering its own element instead would have to repeat every one of those before it
 * could draw a single row.
 *
 * Validates the requested year against the farm's existing fertilizer-years
 * list. When the year is missing from that list the page renders the inline
 * "Düngejahr nicht gefunden" `<fm-error-state>` instead of redirecting, so the
 * URL the user typed or shared remains addressable. A 403 from the farm or
 * year-list endpoint surfaces the "Kein Zugriff auf das Düngejahr"
 * `<fm-error-state>`. Other transport-level failures fall through to a
 * closable callout above the skeleton. A 404 from `getFarmById` and an
 * organisation-fetch failure both redirect upstream so the parent farms /
 * organisations list can render its own error state.
 *
 * Data lives in a single composite `@lit/task` Task keyed on
 * `[organizationId, farmId, year]`. The Task fans out to organisation +
 * farm + year-list fetches in parallel and returns a discriminated union
 * that the renderer branches on. Stale responses are discarded by the
 * Task's built-in `AbortSignal`; there is no manual load token. The area is
 * deliberately absent from the key: moving between areas of one year changes
 * the page's chrome, not the context it validated.
 */
@customElement('fm-fertilizer-year-detail')
export class FmFertilizerYearDetail extends LitElement {

    static styles = [
        detailPageStyles,
        skeletonStyles,
        css`
            .page-header {
                display: flex;
                flex-direction: column;
                gap: var(--wa-space-xs);
                margin-bottom: var(--wa-space-l);
            }

            .lead {
                margin: 0;
                font-size: var(--wa-font-size-s);
                color: var(--fm-color-text-quiet);
            }
        `,
    ];

    @property({
        type: String,
    })
    organizationId = '';

    @property({
        type: String,
    })
    farmId = '';

    @property({
        type: String,
    })
    year = '';

    /**
     * The work area this page stands in for, or `null` for the year itself.
     * Set by the route, never parsed from the URL here: the registry that
     * declares the areas is the same one the routes are generated from.
     */
    @property({
        attribute: false,
    })
    area: IFertilizerYearArea | null = null;

    /**
     * The sub-area addressed below the open area, or the empty string for the
     * area's own address, which shows the area's first sub-area. Set by the
     * route, like `area`.
     */
    @property({
        type: String,
    })
    subArea = '';

    /** Which of the Schlagkartei's screens is addressed. Set by the route, like `area`. */
    @property({
        attribute: false,
    })
    parcelView: ParcelView = parcelListView;

    /** Which of the Bodenuntersuchung's screens the route addresses; its list by default. */
    @property({
        attribute: false,
    })
    soilSampleView: SoilSampleView = soilSampleListView;

    /** Which of the Düngemittel's screens the route addresses; its list by default. */
    @property({
        attribute: false,
    })
    fertilizerProductView: FertilizerProductView = fertilizerProductListView;

    /** Which of the Ertragsangaben's screens the route addresses; its list by default. */
    @property({
        attribute: false,
    })
    yieldSeriesView: YieldSeriesView = yieldSeriesListView;

    /**
     * Which of the Vorjahresdaten's screens the route addresses; its tabs by default. One
     * union for both tabs, whose record carries the tab that says which leaf renders
     * ({@link PreviousYearView}).
     */
    @property({
        attribute: false,
    })
    previousYearView: PreviousYearView = previousYearListView;

    /** Which of the Anbaudaten's screens the route addresses; its list by default. */
    @property({
        attribute: false,
    })
    cultivationDataView: CultivationDataView = cultivationDataListView;

    /**
     * Which screen of the Düngeplanung or the Dokumentation the route addresses, a mineral
     * dose's page or the tabs; the tabs by default.
     */
    @property({
        attribute: false,
    })
    fertilizerApplicationView: FertilizerApplicationView = fertilizerApplicationListView;

    /**
     * What the entity the open screen is about is called, as its leaf reported it. `null`
     * until the leaf's load settles, which is what the heading skeleton stands in for; the
     * page owns no fetch of that entity itself.
     *
     * Which entity that is depends on the area: a parcel under the Schlagkartei and the
     * Bodenuntersuchung, a Düngemittel of either kind under the Düngemittel area. One reading rather than
     * one per area, so the heading, the breadcrumb tail and the stale-report guard cannot
     * disagree about which one is on screen.
     */
    @state()
    private subjectName: string | null = null;

    /**
     * The second fact the screen names about that entity, on the line under the heading,
     * where its leaf reported one: the Bodenprobe's screen says which Schlag it is about.
     *
     * The breadcrumb tail deliberately does without it ({@link IDisplayNameDetail}), so it
     * reaches the lead alone and never {@link screenLabel}. It is what the Bodenuntersuchung
     * declares its own lead as.
     */
    @state()
    private subjectQualifier: string | null = null;

    /** Whether that entity failed to load, so the heading stops waiting for a name. */
    @state()
    private subjectLoadFailed = false;

    /**
     * The Kultur picked on the Ertragsangaben list, on its way to the create screen.
     *
     * Held by the page because the two leaves are siblings under it and the pick happens
     * before the create screen exists: the list answers the Kultur, navigates, and this
     * hands it on. The whole catalog row rather than an id, the catalog answering no
     * single crop, so the create screen could not name one again from one.
     *
     * Dropped as soon as that screen is left ({@link forgetStaleChosenCrop}), so a later
     * arrival at its address cannot open on a Kultur somebody abandoned.
     */
    @state()
    private chosenCrop: ICrop | null = null;

    /**
     * The crop a plan list's sum row was followed from, which the other arm's list of the same
     * area scrolls to. Dropped once that area is left, so a later mount does not scroll.
     */
    @state()
    private planCropFollowed: IFertilizerPlanCropFollowedDetail | null = null;

    /** The area {@link planCropFollowed} was followed in: the fertilizer plan or the documentation. */
    private planCropFollowedArea: string | null = null;

    /**
     * Whether the create screen has actually been rendered with the Kultur above.
     *
     * Load-bearing, and not the same question as "is the create screen addressed": the
     * router resolves a route **asynchronously**, so the update that carries the pick runs
     * a cycle before the one that carries the new address.
     */
    private cropHandedOver = false;

    private validatedSubject: readonly [string, string, string] | null = null;

    /**
     * Which entity the two above were reported for, so a change of subject drops them.
     * `null` whenever no entity is addressed, which is what makes leaving the detail view
     * and coming back to another one a change rather than a match.
     */
    private namedSubjectId: string | null = null;

    /**
     * And which area it was reported under. Part of the key rather than the id alone,
     * because the Schlagkartei and the Bodenuntersuchung address the **same** Schlag: a
     * qualifier the Bodenprobe reported would otherwise stand on the Schlagkartei's own
     * heading, which never asked for one.
     */
    private namedSubjectArea: string | null = null;

    /**
     * Returns the organization ID as `string | null`. The Lit property
     * defaults to `''` when unset; this getter normalizes that to `null`
     * so consumers do not need to special-case the empty-string sentinel.
     * @returns The organization ID or null for orphan farms.
     */
    private get effectiveOrganizationId (): string | null {
        return this.organizationId.length > 0 ? this.organizationId : null;
    }

    /**
     * Builds the canonical farm-detail base path for breadcrumb links and
     * redirects, scoped to either the organization-aware or the standalone
     * farm route family.
     * @returns The farm base path.
     */
    private get basePath (): string {
        if (this.effectiveOrganizationId === null) {
            return `/farms/${this.farmId}`;
        }

        return `/organizations/${this.effectiveOrganizationId}/farms/${this.farmId}`;
    }

    /**
     * The parent farms-listing path for the active scope. Used as the
     * upstream redirect target for the farm 404 / organisation-fetch
     * failures, and as the CTA target for the inline "Kein Zugriff"
     * error state so the user lands back inside the current scope.
     * @returns The parent farms listing path.
     */
    private get parentFarmsPath (): string {
        if (this.effectiveOrganizationId === null) {
            return '/farms';
        }

        return `/organizations/${this.effectiveOrganizationId}/farms`;
    }

    /**
     * Validates the URL inputs (farmId, year format) and redirects on
     * malformed input. Returns the empty-or-bad-format sentinel to the
     * caller when the validation cannot proceed.
     * @returns `null` when the inputs are well-formed; a redirecting
     *   `ValidationResult` otherwise.
     */
    private validateInputs (): ValidationResult | null {
        if (this.farmId === '' || this.year === '') {
            return {
                status: 'redirecting',
            };
        }

        if (!yearPattern.test(this.year)) {
            redirectTo(`${this.basePath}/fertilizer-years`);

            return {
                status: 'redirecting',
            };
        }

        return null;
    }

    /**
     * Handles a failed farm or organization fetch: a missing one redirects to the list it would
     * stand in; every other failure stays on the page, a 403 as „Kein Zugriff“ and anything
     * else as the callout with „Erneut versuchen“.
     * @param failure - The fetch failure.
     * @param upstream - Where a missing record redirects to.
     * @returns The terminal `ValidationResult`.
     */
    private handleScopeFailure (failure: IApiFailure, upstream: string): ValidationResult {
        if (failure.status !== notFoundStatus) {
            return {
                status: 'failure',
                failure,
            };
        }

        redirectTo(upstream);

        return {
            status: 'redirecting',
        };
    }

    /**
     * Resolves org+farm+year-list in parallel, applying redirect
     * side-effects for paths the user cannot reach, and returns a
     * `ValidationResult` for the render layer.
     *
     * A 404 from `getFarmById` or `getOrganization` redirects upstream
     * ({@link handleScopeFailure}); every other failure stays inline.
     * @param signal - Aborted once a newer run supersedes this one.
     * @returns The validation outcome.
     */
    private async runValidation (signal: AbortSignal): Promise<ValidationResult> {
        const guarded = this.validateInputs();

        if (guarded !== null) {
            return guarded;
        }

        const [
            farmResult, yearsResult, orgResult, writable, importable, documentable,
        ] = await Promise.all([
            getFarmById(this.farmId, {
                signal,
            }),
            listFertilizerYearsByFarmId(this.farmId, {
                signal,
            }),
            this.organizationId === '' ?
                Promise.resolve(null) :
                getOrganization(this.organizationId, {
                    signal,
                }),
            this.canWriteFarm(this.farmId),
            this.canImportIntoFarm(this.farmId),
            this.canDocumentFarm(this.farmId),
        ]);

        if (!farmResult.success) {
            return this.handleScopeFailure(farmResult.failure, this.parentFarmsPath);
        }

        if (orgResult !== null && !orgResult.success) {
            return this.handleScopeFailure(orgResult.failure, '/organizations');
        }

        const organization = orgResult === null ? null : orgResult.value;

        return this.classifyYearResult(yearsResult, organization, farmResult.value, {
            writable,
            importable,
            documentable,
        });
    }

    /**
     * Branch helper extracted from `runValidation` to keep that method
     * under the cyclomatic-complexity budget. A missing year renders
     * the "Düngejahr nicht gefunden" inline state instead of
     * redirecting, so the URL stays addressable. A 404 from the
     * year-list endpoint (server message: "Betrieb nicht gefunden.")
     * promotes to the "Betrieb nicht gefunden" inline
     * state — same shape `<fm-farm-detail>` shows when its own primary
     * fetch 404s, so the user sees consistent copy regardless of which
     * endpoint surfaced the missing farm. 403 and other statuses keep
     * the closable-callout path so a transient 5xx can be retried.
     * @param yearsResult - The result of the year-list fetch.
     * @param organization - The resolved organisation (null for orphans).
     * @param farm - The resolved farm.
     * @param access - What the caller may do on the farm, before the year's status narrows it.
     * @returns A discriminated `ValidationResult`.
     */
    private classifyYearResult (
        yearsResult: ApiResult<Array<IFertilizerYear>>,
        organization: IOrganization | null,
        farm: IFarm,
        access: IFarmAccess
    ): ValidationResult {
        if (!yearsResult.success) {
            if (yearsResult.failure.status === notFoundStatus) {
                return {
                    status: 'farm-not-found',
                };
            }

            return {
                status: 'failure',
                failure: yearsResult.failure,
            };
        }

        const fertilizerYear = yearsResult.value.find(entry => entry.year === this.year);

        if (fertilizerYear === undefined) {
            return {
                status: 'year-not-found',
                organization,
                farm,
            };
        }

        return {
            status: 'validated',
            organization,
            farm,
            fertilizerYear,

            // An inactive year is read-only for everyone: it is what "abgeschlossenes
            // Düngejahr" means to the write gate, and no endpoint reopens one.
            canWrite: access.writable && fertilizerYear.status === activeYearStatus,
            canImport: access.importable && fertilizerYear.status === activeYearStatus,
            canDocument: access.documentable && fertilizerYear.status === activeYearStatus,
        };
    }

    /**
     * The three permission predicates the page gates on, held as properties like every read it
     * makes, so the workshop and the specs can answer them (`docs/design/foundations.md` →
     * *Loading seam*).
     */
    @property({
        attribute: false,
    })
    canWriteFarm: typeof canWriteFarm = canWriteFarm;

    @property({
        attribute: false,
    })
    canImportIntoFarm: typeof canImportIntoFarm = canImportIntoFarm;

    @property({
        attribute: false,
    })
    canDocumentFarm: typeof canDocumentFarm = canDocumentFarm;

    private readonly validationTask = new Task(this, {
        task: async (_args, { signal }): Promise<ValidationResult> => this.runValidation(signal),
        args: (): readonly [string, string, string] => [
            this.organizationId,
            this.farmId,
            this.year,
        ],
        onComplete: (result): void => {
            const heading = errorStateHeading(result);

            if (heading !== null) {
                setDocumentTitle(heading);
            }

            this.validatedSubject = result.status === 'validated' ? [
                this.organizationId,
                this.farmId,
                this.year,
            ] : null;
        },
        autoRun: true,
    });

    /**
     * The path of the open year. Doubles as the breadcrumb target for the year
     * segment once the page stands in for one of that year's areas.
     * @returns The year path.
     */
    private get yearPath (): string {
        return `${this.basePath}/fertilizer-years/${this.year}`;
    }

    /**
     * The path of the open area, which is what its leaf builds row and action links from.
     * The empty string while no area is open, which no leaf sees.
     * @returns The area path.
     */
    private get areaPath (): string {
        return this.area === null ? '' : `${this.yearPath}/${this.area.slug}`;
    }

    /**
     * Where a Schlag is made, which the empty card of every Schlag-Liste but the Schlagkartei
     * offers as its next step (`noParcelsYetHint`). Built here rather than in a leaf, because
     * where an area lives is the page's rule, as it is for `subAreaPath`.
     * @returns The Schlagkartei's create address.
     */
    private get parcelCreatePath (): string {
        return `${this.yearPath}/${parcelsAreaSlug}/new`;
    }

    /**
     * Where the Anbaudaten live, which the N-Anpassung N-Kulisse and the Düngeplanung send a
     * reader to for the first Kultur, for the reason above.
     * @returns The area's address.
     */
    private get cultivationDataPath (): string {
        return `${this.yearPath}/${cultivationDataAreaSlug}`;
    }

    /**
     * The sub-area the page shows: the addressed one, or the area's first
     * while the area's own address is open, so that address shows a sub-area
     * without the URL being rewritten. `null` for an area without sub-areas
     * and for the year itself.
     * @returns The effective sub-area.
     */
    private get effectiveSubArea (): IFertilizerYearSubArea | null {
        const subAreas = this.area?.subAreas ?? [];

        return subAreas.find(sub => sub.slug === this.subArea) ?? subAreas.at(0) ?? null;
    }

    /**
     * The sub-areas the strip shows as tabs; a strip of one tab offers no choice.
     * @returns The shown sub-areas, or none where fewer than two are.
     */
    private get shownSubAreas (): ReadonlyArray<IFertilizerYearSubArea> {
        const shown = (this.area?.subAreas ?? []).filter(sub => sub.hidden !== true);

        return shown.length > 1 ? shown : [];
    }

    private renderBreadcrumb (
        farmName: string,
        organization: IOrganization | null,
        views: IEffectiveViews
    ): TemplateResult {
        const orgId = this.effectiveOrganizationId;
        const orgName = organization?.nameWithLegalForm ?? 'Alle';
        const orgHref = orgId === null ? '/organizations' : `/organizations/${orgId}/farms`;
        const { area } = this;
        const items: Array<IBreadcrumbItem> = [
            {
                label: orgName,
                href: orgHref,
            },
            {
                label: farmName,
                href: this.basePath,
            },
            area === null ? {
                label: this.year,
            } : {
                label: this.year,
                href: this.yearPath,
            },
        ];

        items.push(...this.areaTrail(views));

        return html`
            <fm-breadcrumb .items=${items}></fm-breadcrumb>
        `;
    }

    /**
     * The breadcrumb entries below the year: the open area, and whatever the open screen is
     * about once it is about something. The area becomes a link exactly when something hangs
     * beneath it.
     * @param views - The screens actually being rendered.
     * @returns The trailing entries, empty on the year's own page.
     */
    private areaTrail (views: IEffectiveViews): Array<IBreadcrumbItem> {
        const { area } = this;

        if (area === null) {
            return [];
        }

        const tail = this.screenLabel(views);

        if (tail === null) {
            return [
                {
                    label: area.label,
                },
            ];
        }

        return [
            {
                label: area.label,
                href: this.screenParentPath,
            },
            {
                label: tail,
            },
        ];
    }

    /**
     * Where the breadcrumb's area entry leads: the address of the sub-area the open screen
     * hangs under, or the area's own where it hangs under no sub-area.
     *
     * The **label** still names the area rather than the tab, which is the rule every area
     * follows; only the target is narrowed. The Düngemittel's screens hang under two
     * sub-areas, so an area address here would land a Mineraldünger's „Düngemittel“ on the
     * Wirtschaftsdünger tab, which is neither where the record lives nor where every other
     * way out of that screen goes.
     * @returns The address the area entry links.
     */
    private get screenParentPath (): string {
        const slug = this.screenSubAreaSlug;

        return (slug === null ? null : this.subAreaPath(slug)) ?? this.areaPath;
    }

    /**
     * The screens rendered for a caller: each area's addressed view, or its list where it
     * addresses a form the caller may not use. A dose's form is the Dokumentation's where the
     * open area is, so it asks the documentation right there and the write right under the plan.
     * @param canWrite - Whether the caller may write in this fertilizer year.
     * @param canDocument - Whether the caller may write its Dokumentation.
     * @returns The screens.
     */
    private effectiveViews (canWrite: boolean, canDocument: boolean): IEffectiveViews {
        return {
            parcel: resolveEffectiveParcelView(this.parcelView, canWrite),
            soilSample: resolveEffectiveSoilSampleView(this.soilSampleView, canWrite),
            fertilizerProduct: resolveEffectiveFertilizerProductView(this.fertilizerProductView, canWrite),
            previousYear: resolveEffectivePreviousYearView(this.previousYearView, canWrite),
            yieldSeries: resolveEffectiveYieldSeriesView(this.yieldSeriesView, canWrite),
            cultivationData: resolveEffectiveCultivationDataView(this.cultivationDataView, canWrite),
            fertilizerApplication: resolveEffectiveFertilizerApplicationView(
                this.fertilizerApplicationView,
                this.doseKind === 'documented' ? canDocument : canWrite
            ),
        };
    }

    /**
     * The screens rendered right now: the lists until the validation has settled.
     * @returns The screens.
     */
    private get views (): IEffectiveViews {
        const validation = this.validationTask.value;
        const validated = validation?.status === 'validated' ? validation : null;

        return this.effectiveViews(validated?.canWrite ?? false, validated?.canDocument ?? false);
    }

    /**
     * The sub-area the open screen belongs to, which is what the view says rather than what
     * the address does: a screen addressed below a sub-area renders without the tab strip,
     * so `subArea` alone cannot tell the two arms apart on a create form.
     * @returns The slug, or null for an area whose screens hang under no sub-area.
     */
    private get screenSubAreaSlug (): string | null {
        const slug = this.area?.slug;

        if (slug === previousYearAreaSlug) {
            const view = this.views.previousYear;

            return view.kind === 'record' ? view.tab : previousCropsSubAreaSlug;
        }

        if (slug === yieldsAreaSlug) {
            return this.yieldScreenSubAreaSlug;
        }

        if (isDoseArea(slug)) {
            return this.doseScreenSubAreaSlug;
        }

        return slug === fertilizersAreaSlug ? this.fertilizerScreenSubAreaSlug : null;
    }

    /**
     * Which Düngemittel tab the open screen belongs to, read off the arm its view carries.
     *
     * Held apart from {@link screenSubAreaSlug} as {@link yieldScreenSubAreaSlug} is: an
     * area with tabs answers for its own screens.
     * @returns The tab's slug.
     */
    private get fertilizerScreenSubAreaSlug (): string {
        const { kind } = this.views.fertilizerProduct;

        return kind === 'mineralCreate' || kind === 'mineralRecord' ? mineralSubAreaSlug : organicSubAreaSlug;
    }

    /**
     * Which tab the open dose screen belongs to: the mineral one, the only arm with pages of
     * its own.
     * @returns The tab's slug, or null on the area's own address.
     */
    private get doseScreenSubAreaSlug (): string | null {
        return this.views.fertilizerApplication.kind === 'list' ? null : mineralFertilizationSubAreaSlug;
    }

    /**
     * Which Ertragsangaben tab the open screen belongs to, which its record arms carry and
     * its address therefore states.
     *
     * The list arm carries no flag and needs none: it is the area's own address, where the
     * strip is shown and the breadcrumb names no tail, so the first tab is the answer and
     * `yieldSubAreaSlug` gives it for the `false` this reads there.
     * @returns The tab's slug.
     */
    private get yieldScreenSubAreaSlug (): string {
        const view = this.views.yieldSeries;

        return yieldSubAreaSlug(view.kind !== 'list' && view.inNitrateVulnerableZone);
    }

    /**
     * Which entity the open screen addresses, read off the area that owns it: the
     * Schlagkartei's four screens and the Bodenuntersuchung's two hang off a parcel, the
     * Düngemittel's two details off a Wirtschaftsdünger or a Mineraldünger, and each is named
     * by its own leaf's report. `null` on every list and on every placeholder area.
     *
     * The one reading of "the open entity", so the heading, the breadcrumb tail, the name
     * cache and its stale-report guard cannot disagree about which one that is.
     * @returns The entity's id, or null.
     */
    private get addressedSubjectId (): string | null {
        const slug = this.area?.slug;

        if (slug === fertilizersAreaSlug) {
            return addressedFertilizerProductId(this.views.fertilizerProduct);
        }

        if (slug === yieldsAreaSlug) {
            return addressedYieldSeriesId(this.views.yieldSeries);
        }

        if (slug === cultivationDataAreaSlug) {
            return addressedParcelCropId(this.views.cultivationData);
        }

        if (isDoseArea(slug)) {
            return addressedFertilizerApplicationSubjectId(this.views.fertilizerApplication);
        }

        return this.addressedParcelSubjectId(slug);
    }

    /**
     * The same for the three areas whose open record is addressed by its **Schlag**: the
     * Schlagkartei's own, the Bodenprobe of a Schlag and its Vorjahresdaten. Split from the
     * reading above so neither half grows a branch per area past what one function carries.
     * @param slug - The open area's slug.
     * @returns The Schlag's id, or null where none is addressed.
     */
    private addressedParcelSubjectId (slug: string | undefined): string | null {
        if (slug === soilSamplesAreaSlug) {
            return addressedParcelOfView(this.views.soilSample);
        }

        if (slug === previousYearAreaSlug) {
            return addressedParcelOfView(this.views.previousYear);
        }

        const view = this.views.parcel;

        return view.kind === 'record' ? view.parcelId : null;
    }

    /**
     * What the open screen is called, or `null` for a list, which is the area itself. One
     * reading, so the breadcrumb tail and the page heading cannot disagree.
     *
     * Takes the **effective** screen rather than the addressed one, so a caller who may not
     * write and is therefore shown the list is not given a heading and a breadcrumb naming
     * the form they are not being shown. The Bodenuntersuchung and the Vorjahreskulturen need
     * no such reading: both screens of either are the same parcel, so the fallback between
     * them changes nothing the chrome says.
     * @param views - The screens actually being rendered.
     * @returns The label, or null.
     */
    private screenLabel (views: IEffectiveViews): string | null {
        const slug = this.area?.slug;

        if (slug === fertilizersAreaSlug) {
            return this.fertilizerScreenLabel(views.fertilizerProduct);
        }

        if (subjectNamedAreas.includes(slug ?? '')) {
            return this.addressedSubjectId === null ? null : this.subjectHeading;
        }

        if (slug === yieldsAreaSlug) {
            return this.yieldScreenLabel(views.yieldSeries);
        }

        return this.doseOrParcelScreenLabel(views);
    }

    /**
     * The label of the dose areas and the Schlagkartei, split out of {@link screenLabel} for
     * complexity.
     * @param views - The screens actually being rendered.
     * @returns The label, or null.
     */
    private doseOrParcelScreenLabel (views: IEffectiveViews): string | null {
        return isDoseArea(this.area?.slug) ?
            this.doseScreenLabel(views.fertilizerApplication) :
            this.parcelScreenLabel(views.parcel);
    }

    /**
     * The kind of dose the open area holds.
     * @returns Documented under the Dokumentation, planned under the Düngeplanung.
     */
    private get doseKind (): ApplicationKind {
        return this.area?.slug === documentationAreaSlug ? 'documented' : 'planned';
    }

    /**
     * What the open dose screen is called: the dose on its form, the action with its Kultur on
     * the page that adds one.
     * @param view - The dose screen actually being rendered.
     * @returns The label, or null for the tabs.
     */
    private doseScreenLabel (view: FertilizerApplicationView): string | null {
        if (view.kind === 'create') {
            return `${applicationKindCopy[this.doseKind].addApplicationLabel} auf ${
                this.subjectName ?? cropRecordNoun}`;
        }

        return view.kind === 'record' ? this.subjectHeading : null;
    }

    /**
     * What the open Ertragsangaben screen is called.
     *
     * The Kultur on the record screen, which the form leaves out: it is create-only as a
     * field, so the heading is the only place a
     * reader learns whose figures these are.
     * @param view - The Ertragsangaben screen actually being rendered.
     * @returns The label, or null for the list.
     */
    private yieldScreenLabel (view: YieldSeriesView): string | null {
        if (view.kind === 'create') {
            return 'Kultur hinzufügen';
        }

        return view.kind === 'record' ? this.subjectHeading : null;
    }

    /**
     * What the open Düngemittel screen is called.
     * @param view - The Düngemittel screen actually being rendered.
     * @returns The label, or null for the list.
     */
    private fertilizerScreenLabel (view: FertilizerProductView): string | null {
        if (view.kind === 'create') {
            return 'Wirtschaftsdünger anlegen';
        }

        if (view.kind === 'mineralCreate') {
            return 'Mineraldünger anlegen';
        }

        return addressedFertilizerProductId(view) === null ? null : this.subjectHeading;
    }

    /**
     * What the open Schlagkartei screen is called.
     * @param view - The Schlagkartei screen actually being rendered.
     * @returns The label, or null for the list.
     */
    private parcelScreenLabel (view: ParcelView): string | null {
        if (view.kind === 'create') {
            return 'Schlag anlegen';
        }

        if (view.kind === 'duplicate') {
            return 'Schlag duplizieren';
        }

        return view.kind === 'record' ? this.subjectHeading : null;
    }

    /**
     * What names the open entity before its leaf has reported one: the fallback noun of the
     * area that owns it, so a failed load leaves a heading rather than an indefinite
     * skeleton.
     * @returns The heading text.
     */
    private get subjectHeading (): string {
        return this.subjectName ?? this.subjectFallback;
    }

    /**
     * The fallback noun of the open entity, which under the Düngemittel depends on which of
     * the two details is addressed.
     * @returns The noun.
     */
    private get subjectFallback (): string {
        const slug = this.area?.slug;

        if (slug === yieldsAreaSlug) {
            return 'Ertragsreihe';
        }

        if (slug === cultivationDataAreaSlug) {
            return cropRecordNoun;
        }

        if (isDoseArea(slug)) {
            return 'Gabe';
        }

        if (slug !== fertilizersAreaSlug) {
            return 'Schlag';
        }

        return this.views.fertilizerProduct.kind === 'mineralRecord' ? 'Mineraldünger' : 'Wirtschaftsdünger';
    }

    /**
     * Whether the page is still waiting to learn what the open entity is called, which is
     * what the heading skeleton stands in for.
     * @returns Whether the name is still pending.
     */
    private get awaitingSubjectName (): boolean {
        return this.addressedSubjectId !== null && this.subjectName === null && !this.subjectLoadFailed;
    }

    /**
     * Forgets the cached name once the route addresses a different Schlag, so the heading
     * and the breadcrumb fall back to the skeleton rather than naming the one before it.
     * The leaf reports the new name from its own load, which lands after this.
     */
    protected willUpdate (): void {
        this.forgetStaleChosenCrop();

        if (this.area?.slug !== this.planCropFollowedArea) {
            this.planCropFollowed = null;
        }

        const addressed = this.addressedSubjectId;
        const area = this.area?.slug ?? null;

        if (addressed === this.namedSubjectId && area === this.namedSubjectArea) {
            return;
        }

        this.namedSubjectId = addressed;
        this.namedSubjectArea = area;
        this.subjectName = null;
        this.subjectQualifier = null;
        this.subjectLoadFailed = false;
    }

    /**
     * Drops the Kultur handed over by the list once the create screen it was carried to has
     * been left, which is where its life ends: keeping it would open a later „Kultur
     * hinzufügen“ on a Kultur somebody abandoned.
     *
     * Waits for that screen to have been rendered rather than merely addressed; see
     * {@link cropHandedOver} for the race that requires it.
     */
    private forgetStaleChosenCrop (): void {
        if (this.views.yieldSeries.kind === 'create') {
            this.cropHandedOver = true;

            return;
        }

        if (this.cropHandedOver) {
            this.cropHandedOver = false;
            this.chosenCrop = null;
        }
    }

    /**
     * Takes the Kultur the list picked, which the create screen it is navigating to opens
     * on.
     * @param event - The list's report.
     */
    private readonly handleCropChosen = (event: CustomEvent<IYieldSeriesCropChosenDetail>): void => {
        this.chosenCrop = event.detail.crop;
    };

    /**
     * Takes the crop a sum row was followed from.
     * @param event - The list's report.
     */
    private readonly handlePlanCropFollowed = (event: CustomEvent<IFertilizerPlanCropFollowedDetail>): void => {
        this.planCropFollowed = event.detail;
        this.planCropFollowedArea = this.area?.slug ?? null;
    };

    private readonly retryFailedLoad = (): void => {
        void this.validationTask.run();
    };

    /**
     * The loading state. An area with sub-areas reserves its tab strip here too,
     * because the registry decides that strip rather than the load: without the
     * row, the body drops by its height the moment the year validates.
     * @param failure - The failure to raise above the shapes, if the load failed.
     * @returns The skeleton template.
     */
    private renderSkeleton (failure: IApiFailure | null = null): TemplateResult {
        const subAreaCount = this.shownSubAreas.length;

        return liveRegion(html`
            ${failure !== null ? html`
                <fm-load-error-callout description="Das Düngejahr konnte nicht geladen werden."
                                       @retry=${this.retryFailedLoad}>
                </fm-load-error-callout>
            ` : nothing}
            ${headingSkeleton()}
            ${subAreaCount === 0 ? nothing : tabStripRowSkeleton(subAreaCount)}
            ${cardSkeleton()}
        `);
    }

    /**
     * The placeholder's description. The year itself points at the areas below
     * it, an area at the epic that will fill it.
     * @returns The description copy.
     */
    private get placeholderDescription (): string {
        return this.area === null ?
            'Dieser Bereich ist ein Platzhalter. Wählen Sie einen Bereich des Düngejahres in der Navigation.' :
            'Dieser Bereich ist ein Platzhalter.';
    }

    private renderPlaceholder (): TemplateResult {
        return html`
            <fm-empty-state
                .icon=${faPersonDigging}
                heading="Arbeitsbereich noch in Entwicklung"
                description=${this.placeholderDescription}>
            </fm-empty-state>
        `;
    }

    /**
     * The open area's sub-areas as the shared tab strip, each panel filled by the caller.
     *
     * Taking the panel body per sub-area rather than rendering one is what lets an area be
     * **partly** built, the way the Düngemittel was while only its Wirtschaftsdünger tab had
     * a leaf, and an area with no leaf at all passes the placeholder for every one. The panels
     * are the group's own light-DOM children for the reason documented on `detailPageTabs`.
     * @param panelFor - The body of one sub-area's panel.
     * @returns The strip, or the open sub-area's body alone where the strip offers it no tab.
     */
    private renderSubAreaTabs (panelFor: (sub: IFertilizerYearSubArea) => TemplateResult): TemplateResult {
        const subAreas = this.shownSubAreas;
        const active = this.effectiveSubArea;

        if (active === null) {
            return this.renderPlaceholder();
        }

        if (!subAreas.includes(active)) {
            return panelFor(active);
        }

        return detailPageTabs({
            tabs: subAreas.map(sub => ({
                name: sub.slug,
                label: sub.label,
            })),
            active: active.slug,
            pending: false,
            onTabShow: this.handleSubAreaShow,
            panels: html`
                ${subAreas.map(sub => html`
                    <wa-tab-panel name=${sub.slug}>${panelFor(sub)}</wa-tab-panel>
                `)}
            `,
        });
    }

    /**
     * The body of an area whose epic has not landed: the placeholder alone, or the
     * placeholder in every one of its sub-area panels.
     * @returns The placeholder area's body.
     */
    private renderPlaceholderArea (): TemplateResult {
        return this.renderSubAreaTabs((): TemplateResult => this.renderPlaceholder());
    }

    /**
     * The address of one declared sub-area: the area's own address for the
     * first, one segment below it otherwise. An area declaring
     * `addressesEverySubArea` addresses its first the same way as the rest,
     * its own address being a redirect onto that one. `null` for a slug the
     * area does not declare.
     * @param slug - The sub-area slug to resolve.
     * @returns The address, or `null`.
     */
    private subAreaPath (slug: string): string | null {
        const { area } = this;
        const subAreas = area?.subAreas ?? [];
        const index = subAreas.findIndex(sub => sub.slug === slug);

        if (index === -1) {
            return null;
        }

        const own = index > 0 || area?.addressesEverySubArea === true;

        return own ? `${this.areaPath}/${slug}` : this.areaPath;
    }

    /**
     * Handles a sub-area tab the user picked, turning it into navigation.
     * Compared against the effective sub-area rather than the addressed one,
     * for the reason documented on `FmFarmDetail`'s tab handler: the tab
     * group also announces the tab it activates programmatically.
     * @param event - The tab group's announcement.
     */
    private readonly handleSubAreaShow = (event: CustomEvent<{ name: string }>): void => {
        const { name } = event.detail;
        const target = this.subAreaPath(name);

        if (target === null || name === this.effectiveSubArea?.slug) {
            return;
        }

        redirectTo(target);
    };

    /**
     * The body of the open area. The areas that have grown one render their own leaf, which
     * owns its data the way a farm-detail tab leaf does; the rest are still the placeholder,
     * tabbed where the area declares sub-areas. The leaf is chosen here rather than by the
     * route because the route would otherwise have to re-validate the year and rebuild this
     * chrome to render it.
     * @param result - The validated year context.
     * @param views - The screens actually being rendered, which is what the chrome around
     * this body reads too.
     * @returns The area's body.
     */
    private renderAreaBody (result: IValidatedYear, views: IEffectiveViews): TemplateResult {
        const bodies: Readonly<Record<string, () => TemplateResult>> = {
            [parcelsAreaSlug]: () => this.renderParcelBody(result, views.parcel),
            [fertilizersAreaSlug]: () => this.renderFertilizerBody(result, views.fertilizerProduct),
            [soilSamplesAreaSlug]: () => this.renderSoilSampleBody(result, views.soilSample),
            [previousYearAreaSlug]: () => this.renderPreviousYearBody(result, views.previousYear),
            [yieldsAreaSlug]: () => this.renderYieldBody(result, views.yieldSeries),
            [nitrogenAdjustmentAreaSlug]: () => this.renderNitrogenAdjustmentBody(result),
            [cultivationDataAreaSlug]: () => this.renderCultivationDataBody(result, views.cultivationData),
            [fertilizerPlanAreaSlug]: () => this.renderFertilizerPlanBody(result, views.fertilizerApplication),
            [documentationAreaSlug]: () => this.renderDocumentationBody(result, views.fertilizerApplication),
        };
        const body = this.area === null ? undefined : bodies[this.area.slug];

        return body === undefined ? this.renderPlaceholderArea() : body();
    }

    /**
     * The N-Anpassung N-Kulisse's body: its list, the one screen the area has. It edits in a
     * dialog rather than on a page, so no view of its own is passed
     * and its own address is the only one this leaf is shown at.
     * @param result - The validated year context.
     * @returns The area's body.
     */
    private renderNitrogenAdjustmentBody (result: IValidatedYear): TemplateResult {
        return html`
            <fm-nitrogen-adjustment-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                parcelCreatePath=${this.parcelCreatePath}
                cultivationDataPath=${this.cultivationDataPath}
                ?writable=${result.canWrite}>
            </fm-nitrogen-adjustment-list>
        `;
    }

    /**
     * The Anbaudaten's body: the Schläge of the year, each a band over the Kulturen grown on
     * it. The area carries no tabs, so its own address is the only one this leaf is shown at.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The area's body.
     */
    private renderCultivationDataBody (result: IValidatedYear, view: CultivationDataView): TemplateResult {

        if (view.kind === 'record') {
            return html`
                <fm-parcel-crop-detail
                    farmId=${this.farmId}
                    fertilizerYearId=${result.fertilizerYear.id}
                    parcelId=${view.parcelId}
                    parcelCropId=${view.parcelCropId}
                    areaPath=${this.areaPath}
                    ?writable=${result.canWrite}
                    @fm-display-name-changed=${this.handleSubjectName}
                    @fm-display-name-failed=${this.handleSubjectNameFailed}>
                </fm-parcel-crop-detail>
            `;
        }

        return html`
            <fm-parcel-crop-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                year=${result.fertilizerYear.year}
                areaPath=${this.areaPath}
                parcelCreatePath=${this.parcelCreatePath}
                ?writable=${result.canWrite}>
            </fm-parcel-crop-list>
        `;
    }

    /**
     * The fertilizer plan's body: the tab strip, the organic arm in the first panel and the
     * mineral in the second, one leaf told its arm in each.
     *
     * Only a mineral dose has an address of its own, below the mineral tab and rendered alone;
     * the organic doses edit in dialogs, so the area declares no `addressesEverySubArea`.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The area's body.
     */
    private renderFertilizerPlanBody (result: IValidatedYear, view: FertilizerApplicationView): TemplateResult {
        if (view.kind !== 'list') {
            return this.renderMineralDose(result, view, 'planned');
        }

        return this.renderSubAreaTabs((sub): TemplateResult => this.renderFertilizerPlanList(
            result,
            sub.slug === organicFertilizationSubAreaSlug ? 'organic' : 'mineral',
            'planned'
        ));
    }

    /**
     * The documentation's body: the tab strip, the plan list over the documented organic and over
     * the mineral doses in their two panels, the Weidetiere list and the Pflanzenschutz list in
     * theirs. A mineral dose's page renders alone, as under the Düngeplanung.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The area's body.
     */
    private renderDocumentationBody (result: IValidatedYear, view: FertilizerApplicationView): TemplateResult {
        if (view.kind !== 'list') {
            return this.renderMineralDose(result, view, 'documented');
        }

        const tabs: Readonly<Partial<Record<string, () => TemplateResult>>> = {
            [grazingAnimalsSubAreaSlug]: () => this.renderGrazingRecordList(result),
            [plantProtectionSubAreaSlug]: () => this.renderPlantProtectionList(result),
        };

        return this.renderSubAreaTabs((sub): TemplateResult => tabs[sub.slug]?.() ?? this.renderFertilizerPlanList(
            result,
            sub.slug === organicFertilizationSubAreaSlug ? 'organic' : 'mineral',
            'documented'
        ));
    }

    /**
     * The page of a mineral dose: the form that adds one to a Kultur, or the form of one that
     * exists. Both return to the open area's mineral tab.
     * @param result - The validated year context.
     * @param view - The dose screen the route addresses.
     * @param kind - Whether the open area holds planned or documented doses.
     * @returns The screen.
     */
    private renderMineralDose (
        result: IValidatedYear,
        view: Exclude<FertilizerApplicationView, { kind: 'list' }>,
        kind: ApplicationKind
    ): TemplateResult {
        const areaPath = this.subAreaPath(mineralFertilizationSubAreaSlug) ?? this.areaPath;

        if (view.kind === 'create') {
            return html`
                <fm-mineral-dose-create
                    farmId=${this.farmId}
                    fertilizerYearId=${result.fertilizerYear.id}
                    parcelId=${view.parcelId}
                    parcelCropId=${view.parcelCropId}
                    kind=${kind}
                    startDate=${result.fertilizerYear.startDate}
                    endDate=${result.fertilizerYear.endDate}
                    areaPath=${areaPath}
                    ?templatesOffered=${result.canWrite}
                    @fm-display-name-changed=${this.handleSubjectName}
                    @fm-display-name-failed=${this.handleSubjectNameFailed}>
                </fm-mineral-dose-create>
            `;
        }

        return html`
            <fm-mineral-dose-detail
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                parcelId=${view.parcelId}
                parcelCropId=${view.parcelCropId}
                fertilizerApplicationId=${view.fertilizerApplicationId}
                kind=${kind}
                startDate=${result.fertilizerYear.startDate}
                endDate=${result.fertilizerYear.endDate}
                areaPath=${areaPath}
                @fm-display-name-changed=${this.handleSubjectName}
                @fm-display-name-failed=${this.handleSubjectNameFailed}>
            </fm-mineral-dose-detail>
        `;
    }

    /**
     * The Weidetiere tab's list. A Weidetier-Zeile edits in a dialog and carries no address of
     * its own, so the tab hangs no records below it.
     * @param result - The validated year context.
     * @returns The tab's body.
     */
    private renderGrazingRecordList (result: IValidatedYear): TemplateResult {
        return html`
            <fm-grazing-record-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                parcelCreatePath=${this.parcelCreatePath}
                cultivationDataPath=${this.cultivationDataPath}
                ?writable=${result.canDocument}>
            </fm-grazing-record-list>
        `;
    }

    /**
     * The Pflanzenschutz tab's list. A Maßnahme edits in a dialog and carries no address of its
     * own, so the tab hangs no records below it.
     * @param result - The validated year context.
     * @returns The tab's body.
     */
    private renderPlantProtectionList (result: IValidatedYear): TemplateResult {
        return html`
            <fm-plant-protection-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                year=${result.fertilizerYear.year}
                parcelCreatePath=${this.parcelCreatePath}
                cultivationDataPath=${this.cultivationDataPath}
                ?writable=${result.canDocument}>
            </fm-plant-protection-list>
        `;
    }

    /**
     * One arm of the plan list, told where the other arm's tab of the same area lives: its sum
     * row states what that arm planned or documented and links to it. The documentation's lists
     * edit on the documentation right; the templates, whose pick creates a Düngemittel, stay on
     * the write right under either area.
     * @param result - The validated year context.
     * @param arm - Which arm the tab lists.
     * @param kind - Whether it lists the plan or the documentation.
     * @returns The tab's body.
     */
    private renderFertilizerPlanList (
        result: IValidatedYear,
        arm: FertilizerKind,
        kind: ApplicationKind
    ): TemplateResult {
        const otherArm = arm === 'organic' ? mineralFertilizationSubAreaSlug : organicFertilizationSubAreaSlug;

        return html`
            <fm-fertilizer-plan-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                arm=${arm}
                kind=${kind}
                startDate=${result.fertilizerYear.startDate}
                endDate=${result.fertilizerYear.endDate}
                otherArmPath=${this.subAreaPath(otherArm) ?? ''}
                mineralDosePath=${this.subAreaPath(mineralFertilizationSubAreaSlug) ?? ''}
                parcelCreatePath=${this.parcelCreatePath}
                cultivationDataPath=${this.cultivationDataPath}
                .reveal=${this.planCropFollowed?.arm === arm ? this.planCropFollowed : null}
                ?writable=${kind === 'documented' ? result.canDocument : result.canWrite}
                ?templatesOffered=${result.canWrite}
                @fm-fertilizer-plan-crop-followed=${this.handlePlanCropFollowed}>
            </fm-fertilizer-plan-list>
        `;
    }

    /**
     * The Vorjahresdaten's body: at either sub-area address the tab strip, with the
     * Vorjahreskulturen list in the first panel and the Organische Düngung list in the second,
     * both mounted whichever tab is shown.
     *
     * **A screen addressed below the area renders alone**, the strip belonging to the area's
     * own address (`docs/design/pages.md` → *Sidebar work areas (Bereiche)*). This is that
     * check, and the detail's tab says which of the two leaves it is.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The area's body.
     */
    private renderPreviousYearBody (result: IValidatedYear, view: PreviousYearView): TemplateResult {

        if (view.kind === 'record') {
            return view.tab === previousCropsSubAreaSlug ?
                this.renderPrecedingCropDetail(result, view) :
                this.renderPriorOrganicApplicationDetail(result, view);
        }

        return this.renderSubAreaTabs((sub): TemplateResult => (
            sub.slug === previousCropsSubAreaSlug ?
                this.renderPrecedingCropList(result) :
                this.renderPriorOrganicApplicationList(result)
        ));
    }

    /**
     * The Organische Düngung tab's list.
     * @param result - The validated year context.
     * @returns The tab's body.
     */
    private renderPriorOrganicApplicationList (result: IValidatedYear): TemplateResult {
        return html`
            <fm-prior-organic-application-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                areaPath=${this.subAreaRecordsPath(organicFertilizationSubAreaSlug)}
                parcelCreatePath=${this.parcelCreatePath}
                ?writable=${result.canWrite}>
            </fm-prior-organic-application-list>
        `;
    }

    /**
     * One Schlag's organische Düngung der Vorjahre, addressed below the tab: the form
     * that writes it. A Schlag with no Satz reaches the same form, so there is no
     * create arm.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The screen.
     */
    private renderPriorOrganicApplicationDetail (
        result: IValidatedYear,
        view: PreviousYearRecordView
    ): TemplateResult {
        return html`
            <fm-prior-organic-application-detail
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                parcelId=${view.parcelId}
                areaPath=${this.subAreaRecordsPath(organicFertilizationSubAreaSlug)}
                ?writable=${result.canWrite}
                @fm-display-name-changed=${this.handleSubjectName}
                @fm-display-name-failed=${this.handleSubjectNameFailed}>
            </fm-prior-organic-application-detail>
        `;
    }

    /**
     * The Vorjahreskulturen tab's list.
     * @param result - The validated year context.
     * @returns The tab's body.
     */
    private renderPrecedingCropList (result: IValidatedYear): TemplateResult {
        return html`
            <fm-preceding-crop-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                year=${result.fertilizerYear.year}
                areaPath=${this.subAreaRecordsPath(previousCropsSubAreaSlug)}
                parcelCreatePath=${this.parcelCreatePath}
                ?writable=${result.canWrite}>
            </fm-preceding-crop-list>
        `;
    }

    /**
     * One Schlag's Vorjahreskultur, addressed below the tab: the form that
     * writes it. A Schlag with no record reaches the same form, so there is no create arm.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The screen.
     */
    private renderPrecedingCropDetail (
        result: IValidatedYear,
        view: PreviousYearRecordView
    ): TemplateResult {
        return html`
            <fm-preceding-crop-detail
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                year=${result.fertilizerYear.year}
                parcelId=${view.parcelId}
                areaPath=${this.subAreaRecordsPath(previousCropsSubAreaSlug)}
                ?writable=${result.canWrite}
                @fm-display-name-changed=${this.handleSubjectName}
                @fm-display-name-failed=${this.handleSubjectNameFailed}>
            </fm-preceding-crop-detail>
        `;
    }

    /**
     * The Düngemittel's body: at either sub-area address the tab strip, with the
     * Wirtschaftsdünger list in one panel and the `Individuelle Mineraldünger` list in the
     * other, both mounted whichever tab is shown.
     *
     * **A screen addressed below the area renders alone**, the strip belonging to the area's
     * own address (`docs/design/pages.md` → *Sidebar work areas*). Every area with sub-areas owes
     * the same check against its own view union; here the union carries both kinds, so the
     * mineral screens are told apart first.
     * @param result - The validated year context.
     * @param view - The Düngemittel screen actually being rendered.
     * @returns The area's body.
     */
    private renderFertilizerBody (result: IValidatedYear, view: FertilizerProductView): TemplateResult {
        if (view.kind === 'mineralCreate' || view.kind === 'mineralRecord') {
            return this.renderMineralFertilizerBody(result, view);
        }

        if (view.kind !== 'list') {
            return this.renderOrganicFertilizerBody(result, view);
        }

        return this.renderSubAreaTabs((sub): TemplateResult => (
            sub.slug === organicSubAreaSlug ?
                this.renderOrganicFertilizerBody(result, view) :
                this.renderMineralFertilizerList(result)
        ));
    }

    /**
     * The address one sub-area's records hang under, which its leaf uses for row links, the
     * create target and the way back.
     *
     * Always one segment below the area, on **both** Düngemittel arms and on both
     * Vorjahresdaten tabs: each area declares `addressesEverySubArea`, so its first tab is
     * addressed like the rest rather than standing at the area's own address. Distinct from
     * {@link subAreaPath}, which answers where the tab itself lives.
     * @param slug - The sub-area whose records are wanted.
     * @returns The address the records hang under.
     */
    private subAreaRecordsPath (slug: string): string {
        return `${this.areaPath}/${slug}`;
    }

    /**
     * The `Individuelle Mineraldünger` tab's list.
     * @param result - The validated year context.
     * @returns The tab's body.
     */
    private renderMineralFertilizerList (result: IValidatedYear): TemplateResult {
        return html`
            <fm-mineral-fertilizer-product-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                areaPath=${this.subAreaRecordsPath(mineralSubAreaSlug)}
                ?writable=${result.canWrite}
                ?importable=${result.canImport}>
            </fm-mineral-fertilizer-product-list>
        `;
    }

    /**
     * The mineral screens addressed below the sub-area: one Mineraldünger, or the form that
     * creates one. A caller who may not write never reaches the create arm, for the reason
     * {@link renderOrganicFertilizerBody} states.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The screen.
     */
    private renderMineralFertilizerBody (
        result: IValidatedYear,
        view: Extract<FertilizerProductView, { kind: 'mineralCreate' | 'mineralRecord' }>
    ): TemplateResult {
        if (view.kind === 'mineralRecord') {
            return html`
                <fm-mineral-fertilizer-product-detail
                    farmId=${this.farmId}
                    fertilizerYearId=${result.fertilizerYear.id}
                    fertilizerProductId=${view.fertilizerProductId}
                    areaPath=${this.subAreaRecordsPath(mineralSubAreaSlug)}
                    @fm-display-name-changed=${this.handleSubjectName}
                    @fm-display-name-failed=${this.handleSubjectNameFailed}>
                </fm-mineral-fertilizer-product-detail>
            `;
        }

        return html`
            <fm-mineral-fertilizer-product-create
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                areaPath=${this.subAreaRecordsPath(mineralSubAreaSlug)}>
            </fm-mineral-fertilizer-product-create>
        `;
    }

    /**
     * The `Wirtschaftsdünger` tab: one Düngemittel, the form that creates one, or the list.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The tab's body.
     */
    private renderOrganicFertilizerBody (result: IValidatedYear, view: FertilizerProductView): TemplateResult {
        const recordsPath = this.subAreaRecordsPath(organicSubAreaSlug);

        if (view.kind === 'record') {
            return html`
                <fm-fertilizer-product-detail
                    farmId=${this.farmId}
                    fertilizerYearId=${result.fertilizerYear.id}
                    year=${this.year}
                    fertilizerProductId=${view.fertilizerProductId}
                    areaPath=${recordsPath}
                    @fm-display-name-changed=${this.handleSubjectName}
                    @fm-display-name-failed=${this.handleSubjectNameFailed}>
                </fm-fertilizer-product-detail>
            `;
        }

        // A caller who may not write never reaches this arm: the effective view is the list,
        // which is where a deep link to the create path lands, chrome included.
        if (view.kind === 'create') {
            return html`
                <fm-fertilizer-product-create
                    farmId=${this.farmId}
                    fertilizerYearId=${result.fertilizerYear.id}
                    year=${this.year}
                    areaPath=${recordsPath}>
                </fm-fertilizer-product-create>
            `;
        }

        return html`
            <fm-fertilizer-product-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                areaPath=${recordsPath}
                ?writable=${result.canWrite}>
            </fm-fertilizer-product-list>
        `;
    }

    /**
     * The Ertragsangaben's body: at the area's own address the tab strip, each panel
     * carrying its window's list; below either tab one Ertragsreihe or the page that
     * creates one.
     *
     * **A screen addressed below a tab renders alone**, the strip belonging to the area's
     * own address. Which window such a screen shows is the address's to say, and the route
     * says it: nothing here reads a default, so neither tab can be reached on the other's
     * six years.
     *
     * A panel's window is which panel it is, the strip rendering both at once; a record
     * screen's is what its route declared. The window itself is resolved once, by
     * {@link yieldWindow}, and handed to whichever screen renders, which is what keeps the
     * second tab wiring rather than a second set of screens.
     * @param result - The validated year context.
     * @param view - The Ertragsangaben screen actually being rendered.
     * @returns The area's body.
     */
    private renderYieldBody (result: IValidatedYear, view: YieldSeriesView): TemplateResult {
        if (view.kind === 'create') {
            return this.renderYieldCreate(result, view.inNitrateVulnerableZone);
        }

        if (view.kind === 'record') {
            return this.renderYieldDetail(result, view, view.inNitrateVulnerableZone);
        }

        return this.renderSubAreaTabs((sub): TemplateResult => this.renderYieldList(
            result,
            sub.slug === nitrateVulnerableZoneSubAreaSlug
        ));
    }

    /**
     * The address one Ertragsangaben tab's records hang under, which its screens use for
     * row links, the create target and every way back out.
     *
     * {@link subAreaPath} is the one rule for where a tab lives, and the records of these
     * two hang directly under their tab, so this is that answer rather than a second one:
     * the area's own address for the first tab, one segment below it for the N-Kulisse.
     * Distinct from {@link subAreaRecordsPath}, which serves the two areas whose every tab
     * carries an address of its own.
     * @param inNitrateVulnerableZone - Which of the two tabs is wanted.
     * @returns The address the tab's records hang under.
     */
    private yieldTabPath (inNitrateVulnerableZone: boolean): string {
        return this.subAreaPath(yieldSubAreaSlug(inNitrateVulnerableZone)) ?? this.areaPath;
    }

    /**
     * The page that creates one Ertragsreihe.
     *
     * The flag is stated beside the years and never left to the screen's own default; the
     * endpoint requires it (`api_client/yield_series.ts`). Both come from the arm the
     * address named, so „Kultur hinzufügen“ in either tab writes a series that appears in
     * the tab it was written from.
     * @param result - The validated year context.
     * @param inNitrateVulnerableZone - Which of the two windows this series belongs to.
     * @returns The screen.
     */
    private renderYieldCreate (result: IValidatedYear, inNitrateVulnerableZone: boolean): TemplateResult {
        return html`
            <fm-yield-series-create
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                .fertilizerYear=${Number(this.year)}
                areaPath=${this.yieldTabPath(inNitrateVulnerableZone)}
                ?inNitrateVulnerableZone=${inNitrateVulnerableZone}
                .crop=${this.chosenCrop}
                .years=${this.yieldWindow(inNitrateVulnerableZone)}>
            </fm-yield-series-create>
        `;
    }

    /**
     * One Ertragsreihe: its form.
     *
     * It takes the window rather than the flag alone for the reason
     * {@link renderYieldCreate} does, though it sends no flag of its own: replacing a
     * series states neither the crop nor the flag, both being create-only.
     * @param result - The validated year context.
     * @param view - The record arm of the screen the route addresses.
     * @param inNitrateVulnerableZone - Which of the two windows the series belongs to.
     * @returns The screen.
     */
    private renderYieldDetail (
        result: IValidatedYear,
        view: Extract<YieldSeriesView, { kind: 'record' }>,
        inNitrateVulnerableZone: boolean
    ): TemplateResult {
        return html`
            <fm-yield-series-detail
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                yieldSeriesId=${view.yieldSeriesId}
                areaPath=${this.yieldTabPath(inNitrateVulnerableZone)}
                .years=${this.yieldWindow(inNitrateVulnerableZone)}
                @fm-display-name-changed=${this.handleSubjectName}
                @fm-display-name-failed=${this.handleSubjectNameFailed}>
            </fm-yield-series-detail>
        `;
    }

    /**
     * One window's list of Ertragsreihen. The same list serves either tab over a different
     * window, which is what the flag and the six years being properties are for, so the two
     * panels differ in three bindings and in nothing else.
     * @param result - The validated year context.
     * @param inNitrateVulnerableZone - Which of the two windows this tab shows.
     * @returns The tab's body.
     */
    private renderYieldList (result: IValidatedYear, inNitrateVulnerableZone: boolean): TemplateResult {
        return html`
            <fm-yield-series-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                .fertilizerYear=${Number(this.year)}
                areaPath=${this.yieldTabPath(inNitrateVulnerableZone)}
                ?writable=${result.canWrite}
                ?importable=${result.canImport}
                ?inNitrateVulnerableZone=${inNitrateVulnerableZone}
                .years=${this.yieldWindow(inNitrateVulnerableZone)}
                @fm-yield-series-crop-chosen=${this.handleCropChosen}>
            </fm-yield-series-list>
        `;
    }

    /**
     * The six years one window covers, resolved once here and handed down.
     *
     * Resolved by the page rather than by the screens below it, so the N-Kulisse tab stays
     * wiring: its window is fixed at 2019 down to 2014 and does not move with the
     * Düngejahr, and a second rule anywhere below would answer the other tab's years.
     * @param inNitrateVulnerableZone - Which window is wanted.
     * @returns The six years, newest first.
     */
    private yieldWindow (inNitrateVulnerableZone: boolean): ReadonlyArray<number> {
        return resolveYieldWindow(Number(this.year), inNitrateVulnerableZone);
    }

    /**
     * The Bodenuntersuchung's body: one Schlag's Bodenprobe, or the list of them all.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The area's body.
     */
    private renderSoilSampleBody (result: IValidatedYear, view: SoilSampleView): TemplateResult {

        if (view.kind === 'record') {
            return html`
                <fm-soil-sample-detail
                    farmId=${this.farmId}
                    fertilizerYearId=${result.fertilizerYear.id}
                    parcelId=${view.parcelId}
                    areaPath=${this.areaPath}
                    @fm-display-name-changed=${this.handleSubjectName}
                    @fm-display-name-failed=${this.handleSubjectNameFailed}>
                </fm-soil-sample-detail>
            `;
        }

        return html`
            <fm-soil-sample-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                areaPath=${this.areaPath}
                parcelCreatePath=${this.parcelCreatePath}
                ?writable=${result.canWrite}>
            </fm-soil-sample-list>
        `;
    }

    /**
     * The Schlagkartei's body: one of its four screens, picked by the view the route
     * addressed.
     * @param result - The validated year context.
     * @param view - The screen actually being rendered.
     * @returns The Schlagkartei's body.
     */
    private renderParcelBody (result: IValidatedYear, view: ParcelView): TemplateResult {
        if (view.kind === 'record') {
            return html`
                <fm-parcel-master-data
                    farmId=${this.farmId}
                    fertilizerYearId=${result.fertilizerYear.id}
                    parcelId=${view.parcelId}
                    areaPath=${this.areaPath}
                    @fm-display-name-changed=${this.handleSubjectName}
                    @fm-display-name-failed=${this.handleSubjectNameFailed}>
                </fm-parcel-master-data>
            `;
        }

        if (view.kind === 'create' || view.kind === 'duplicate') {

            // A caller who may not write never reaches this arm at all: the effective view
            // is the list, which is where a deep link to either path lands, chrome included.
            // One element for both, because duplicating *is* the create form: the source it
            // opens on is a property of that form rather than a screen of its own.
            return html`
                <fm-parcel-create
                    farmId=${this.farmId}
                    fertilizerYearId=${result.fertilizerYear.id}
                    areaPath=${this.areaPath}
                    sourceParcelId=${view.kind === 'duplicate' ? view.sourceParcelId : ''}>
                </fm-parcel-create>
            `;
        }

        return this.renderList(result);
    }

    private renderList (result: IValidatedYear): TemplateResult {
        return html`
            <fm-parcel-list
                farmId=${this.farmId}
                fertilizerYearId=${result.fertilizerYear.id}
                areaPath=${this.areaPath}
                ?writable=${result.canWrite}
                ?importable=${result.canImport}>
            </fm-parcel-list>
        `;
    }

    /**
     * Caches the open Schlag's name for the breadcrumb and the heading. Guarded on the
     * addressed id, so a dispatch from a previous mount of the leaf cannot name the Schlag
     * the page has already left.
     * @param event - The leaf's report.
     */
    private readonly handleSubjectName = (event: CustomEvent<IDisplayNameDetail>): void => {
        if (!this.addresses(event.detail.entityId)) {
            return;
        }

        this.subjectName = event.detail.name;
        this.subjectQualifier = event.detail.qualifier ?? null;
        this.subjectLoadFailed = false;
    };

    private readonly handleSubjectNameFailed = (event: CustomEvent<IDisplayNameFailedDetail>): void => {
        if (!this.addresses(event.detail.entityId)) {
            return;
        }

        this.subjectLoadFailed = true;
    };

    /**
     * Whether the page is currently showing the entity a leaf is reporting about, which is
     * what keeps a dispatch from a previous mount of the leaf from naming the wrong one.
     * @param entityId - The id the leaf reported.
     * @returns Whether that is the entity on screen.
     */
    private addresses (entityId: string): boolean {
        return this.addressedSubjectId === entityId;
    }

    /**
     * The page heading: the year, the area, or whatever the open screen is about once it is
     * about something.
     * @param views - The screens actually being rendered.
     * @returns The heading, or its skeleton while the entity's name is pending.
     */
    private renderHeading (views: IEffectiveViews): TemplateResult {
        if (this.awaitingSubjectName) {
            return headingSkeleton();
        }

        const { area } = this;
        const label = this.screenLabel(views);

        if (label !== null) {
            return html`<h1>${label}</h1>`;
        }

        return html`<h1>${area === null ? `Düngejahr ${this.year}` : area.label}</h1>`;
    }

    /**
     * The line under the heading: which Schlag a Bodenprobe is about, what a create form is
     * for, and the context of an area that shows nothing yet. Every other screen carries a
     * section heading instead.
     *
     * The page states no line of its own. It resolves what one is written from and hands
     * that to the open area's own `lead` (`libraries/fertilizer_year_areas.ts`), so an
     * area's line stands beside its label and a screen's beside the route that addressed it.
     * The views are the **effective** ones, which is what takes a create form's line away
     * with the form when the caller may not write.
     * @param result - The validated year context.
     * @param views - The screens actually being rendered.
     * @returns The lead, or null.
     */
    private screenLead (result: IValidatedYear, views: IEffectiveViews): string | null {
        return this.area?.lead({
            farmName: result.farm.nameWithLegalForm,
            year: this.year,
            parcelView: views.parcel,
            fertilizerProductView: views.fertilizerProduct,
            yieldSeriesView: views.yieldSeries,
            subjectQualifier: this.subjectQualifier,
        }) ?? null;
    }

    /**
     * @param lead - What the open screen says under its heading, or null for one that says
     * nothing.
     * @returns The lead, or nothing.
     */
    private renderLead (lead: string | null): TemplateResult | typeof nothing {
        return lead === null ? nothing : html`<p class="lead">${lead}</p>`;
    }

    private renderValidated (result: IValidatedYear): TemplateResult {

        // Resolved once for the whole screen: the body, the heading, the lead and the
        // breadcrumb all describe the same views, and a fallback reaching only the body is a
        // page promising a form it is not showing.
        const views = this.effectiveViews(result.canWrite, result.canDocument);

        return html`
            ${this.renderBreadcrumb(result.farm.nameWithLegalForm, result.organization, views)}
            <div class="page-header">
                ${this.renderHeading(views)}
                ${this.renderLead(this.screenLead(result, views))}
            </div>
            ${this.renderAreaBody(result, views)}
        `;
    }

    /**
     * The "Kein Zugriff auf das Düngejahr" state. Rendered when
     * `getFarmById` or `listFertilizerYearsByFarmId` returned 403. The
     * CTA targets the parent farms list of the current scope (a year-
     * list URL for the same farm would also 403).
     * @returns The forbidden-state template.
     */
    private renderForbidden (): TemplateResult {
        return html`
            <fm-error-state
                tone="forbidden"
                .icon=${faLock}
                heading=${yearForbiddenHeading}
                description="Sie haben keinen Zugriff auf dieses Düngejahr."
                action-href=${this.parentFarmsPath}
                action-label="Zu den Betrieben">
            </fm-error-state>
        `;
    }

    /**
     * The "Düngejahr nicht gefunden" state. Rendered when the
     * year-list endpoint returned 200 but the URL year was not in the
     * list. The CTA targets the farm's year list so the user can
     * pick an existing year.
     * @returns The year-not-found state template.
     */
    private renderYearNotFound (): TemplateResult {
        return html`
            <fm-error-state
                tone="danger"
                .icon=${faCircleExclamation}
                heading=${yearNotFoundHeading}
                description="Dieses Düngejahr existiert nicht."
                action-href=${`${this.basePath}/fertilizer-years`}
                action-label="Zu den Düngejahren">
            </fm-error-state>
        `;
    }

    /**
     * The "Betrieb nicht gefunden" state. Rendered when the
     * year-list endpoint returned 404 (server message: "Betrieb nicht
     * gefunden."). This is the same shape `<fm-farm-detail>` shows on
     * its own 404, so the user sees consistent copy regardless of
     * which endpoint surfaced the missing farm. In practice
     * `getFarmById` 404s first when the farm is genuinely absent and
     * `handleScopeFailure` redirects upstream; this state only fires
     * on an inconsistent server response where the farm endpoint
     * succeeds but the year-list endpoint reports the farm as
     * missing. The CTA targets the parent farms list of the current
     * scope (orphan or organisation).
     * @returns The farm-not-found state template.
     */
    private renderFarmNotFound (): TemplateResult {
        return html`
            <fm-error-state
                tone="danger"
                .icon=${faCircleExclamation}
                heading=${farmNotFoundHeading}
                description="Dieser Betrieb existiert nicht oder wurde gelöscht."
                action-href=${this.parentFarmsPath}
                action-label="Zu den Betrieben">
            </fm-error-state>
        `;
    }

    private renderResult (result: ValidationResult): TemplateResult {
        switch (result.status) {
            case 'validated':
                return this.renderValidated(result);
            case 'year-not-found':
                return this.renderYearNotFound();
            case 'farm-not-found':
                return this.renderFarmNotFound();
            case 'failure':
                if (result.failure.status === forbiddenStatus) {
                    return this.renderForbidden();
                }

                return this.renderSkeleton(result.failure);
            case 'redirecting':
            default:
                return this.renderSkeleton();
        }
    }

    private cachedValidatedResult (): IValidatedYear | null {
        const result = this.validationTask.value;
        const subject = this.validatedSubject;

        if (result?.status !== 'validated' || subject === null) {
            return null;
        }

        return subject[0] === this.organizationId && subject[1] === this.farmId && subject[2] === this.year ?
            result : null;
    }

    render (): TemplateResult {
        return this.validationTask.render({
            initial: () => this.renderSkeleton(),
            pending: () => {
                const result = this.cachedValidatedResult();

                return result === null ? this.renderSkeleton() : this.renderValidated(result);
            },
            complete: result => this.renderResult(result),
            error: () => this.renderSkeleton(),
        });
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'fm-fertilizer-year-detail': FmFertilizerYearDetail;
    }
}
